import axios from "axios";
import { useToast } from "primevue/usetoast";
import { Ref, ref } from "vue";

import {
  ApiInjectionKey,
  safeInject,
  stripPhoneNumber,
  toRailsDateTime,
} from "@/utilities";
import { useForm } from "@/utilities/validations";

import { HolidayLeave } from "./types/holidayLeave";

export function useSubmission(
  form: Ref<HolidayLeave>,
  submitForm: ReturnType<typeof useForm>["submit"],
  setFieldErrorMessage: ReturnType<typeof useForm>["setFieldErrorMessage"]
) {
  const api = safeInject(ApiInjectionKey);

  const loading = ref(false);
  const toast = useToast();

  function showError(
    header = undefined as undefined | string,
    text = undefined as undefined | string
  ) {
    toast.add({
      severity: "error",
      summary: header ?? "Errors in form",
      detail: text ?? "You seem to have a few errors in your form",
      life: 5000,
    });
  }

  const submit = submitForm(
    async () => {
      try {
        loading.value = true;

        const data = await api.holidayLeaveResponses.create({
          data: sanatize(form.value),
        });

        window.location = data.location;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          const { data } = err.response;

          if (data.errors) {
            showError("Error", data.errors.join(", "));
            return;
          }

          Object.entries(data).forEach(([key, val]) => {
            if (["leave.leave_depart", "leave.leave_return"].includes(key)) {
              setFieldErrorMessage("leaveDepart", (val as string[]).join(", "));
            } else {
              setFieldErrorMessage(
                key.replace("leave.", "") as any,
                (val as string[]).join(", ")
              );
            }
          });

          showError();
        }
      } finally {
        loading.value = false;
      }
    },
    () => {
      showError();
    }
  );

  return { submit, loading };
}

function sanatize(form: HolidayLeave) {
  let holidayLeaveResponse;

  switch (form.holidayPlans) {
    case "T":
      holidayLeaveResponse = {
        ...form,
      };
      break;
    case "S":
      holidayLeaveResponse = {
        ...form,
      };
      break;
    case "H":
      holidayLeaveResponse = {
        holidayLeaveId: form.holidayLeaveId,
        holidayPlans: form.holidayPlans,
        isReturning: form.isReturning,
        leaveAttributes: {
          cellphone: stripPhoneNumber(form.cellphone),
          transport: form.transport,
          leaveDepart: toRailsDateTime(form.leaveDepart!),
          leaveReturn: form.isReturning
            ? toRailsDateTime(form.leaveReturn!)
            : null,
        },
      };
      break;
    case "E":
      holidayLeaveResponse = {
        holidayLeaveId: form.holidayLeaveId,
        holidayPlans: form.holidayPlans,
        isReturning: form.isReturning,
        leaveAttributes: {
          leaveDepart: toRailsDateTime(form.leaveDepart!),
          leaveReturn: form.isReturning
            ? toRailsDateTime(form.leaveReturn!)
            : null,
          destination: form.destination,
          hostRelationship: form.hostRelationship,
          hostAge: form.hostAge,
          address: form.address,
          city: form.city,
          state: form.state,
          postal: form.postal,
          driver: form.driver,
          transport: form.transport,
          companions: form.companions,
          reason: form.reason,
          carOwner: form.carOwner,
          ccParent: form.ccParent,
          hostphone: stripPhoneNumber(form.hostphone),
          cellphone: stripPhoneNumber(form.cellphone),
        },
      };
      break;
  }

  return holidayLeaveResponse;
}
